import React from 'react'

import { SwiperSlide } from 'swiper/react/swiper-react.js'
import { useNavigate } from '@reach/router'

import { useSliderSwipe } from 'src/shared/hooks'
import { sliderInMainSettings } from 'src/shared/constants'

import { HBox, Wrapper } from 'src/ui/layout'
import { CustomSliderContainer } from 'src/ui/atoms'
import { AchievementCard, Button, MainAchievementCard } from 'src/ui/molecules'
import { SwiperSlider } from 'src/ui/organisms'
import { styled, useTheme } from 'src/ui/theme'
import { Body, Header } from 'src/ui/typography'

import type { TSliderText } from './types'

import { achivements, videos } from './constants'

const Grid1 = styled.div`
  width: 100%;
  height: 482px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 64px;
`

const GridItem = styled.div`
  width: 50%;
  height: 462px;
`

const Container = styled(CustomSliderContainer)`
  position: relative;
  height: 462px;
  .swiper-pagination-hero {
    position: absolute;
    width: initial;
    left: 40px;
    top: 40px;
    align-items: start;
  }

  button.swiper-pagination-bullet svg circle#spinner {
    animation-duration: 6s;
  }
`

const SliderItem = styled.div`
  height: 462px;
`

const Video = styled.video`
  display: block;
  position: absolute !important;
  object-fit: cover;
  right: 0;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  margin: 0 auto;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  align-items: stretch;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;

  width: 100%;
  height: 100%;
  z-index: 3;
`
const ColorTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #4f4ff4;
  mix-blend-mode: hard-light;
  z-index: 1;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: end;
  flex-grow: 1;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 40px;
`

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const StyledBody = styled(Body)`
  font-size: 14px;
  line-height: 130%;
  letter-spacing: 0%;
  white-space: pre-line;
`

const Title = styled.span`
  white-space: pre-line;
`

const StyledHeader = styled(Header)`
  font-size: 32px;
  line-height: 90%;
  letter-spacing: 2%;
`

const Achievements = styled.div`
  padding: 64px;
  padding-top: 24px;
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 24px;

  & > * {
    flex: 1;
  }
`

type Props = {
  sliderText: TSliderText
  images: TImages
}

export const HeroBlock = ({ images, sliderText }: Props) => {
  const theme = useTheme()
  const { sliderRef, handleMouseDown, handleMouseUp } = useSliderSwipe()
  const navigate = useNavigate()

  const { title, text } = sliderText

  return (
    <>
      <Grid1>
        <GridItem>
          <Container>
            <SwiperSlider
              key="hero"
              paginationEl=".swiper-pagination-hero"
              ref={sliderRef}
              settings={sliderInMainSettings}
              customPagination
            >
              {videos.map(({ src }, index) => {
                if (src) {
                  return (
                    <SwiperSlide key={index}>
                      <SliderItem>
                        <Wrapper>
                          <Video loop playsInline key={src} autoPlay muted>
                            <source src={src} type="video/mp4" />
                            Your browser does not support the video tag.
                          </Video>
                        </Wrapper>
                      </SliderItem>
                    </SwiperSlide>
                  )
                }
              })}
            </SwiperSlider>
            <ContentWrapper
              tabIndex={0}
              onMouseUp={handleMouseUp}
              onMouseDown={handleMouseDown}
            >
              <Content>
                <div className="swiper-pagination-hero"></div>

                <InfoBlock>
                  <StyledHeader size="h2" color={theme.colors.white.primary}>
                    <Title>{title}</Title>
                  </StyledHeader>

                  <HBox height={16} />
                  <StyledBody color={theme.colors.white.primary}>
                    {text}
                  </StyledBody>
                  <HBox height={24} />
                  <Button
                    variant="contrast"
                    onClick={() => navigate('/career/')}
                  >
                    Написать нам
                  </Button>
                </InfoBlock>
              </Content>
            </ContentWrapper>
            <ColorTop />
          </Container>
        </GridItem>
        <GridItem>
          <MainAchievementCard />
        </GridItem>
      </Grid1>
      <Achievements>
        {achivements.map((item, index) => (
          <AchievementCard
            icon={<item.icon />}
            title={item.title}
            subtitle={item.subtitle}
            description={item.description}
            link={item.link}
            key={index}
          />
        ))}
      </Achievements>
    </>
  )
}
